<template>
    <Teleport to="body">
        <Transition name="video-modal">
            <div v-if="visible" class="video-modal__overlay" @click.self="handleClose">
                <div class="video-modal__container">
                    <!-- 头部：标题 + 关闭按钮同行 -->
                    <div class="video-modal__header">
                        <h2 v-if="title" class="video-modal__title">{{ title }}</h2>
                        <button class="video-modal__close" type="button" aria-label="Close" @click="handleClose">
                            <X />
                        </button>
                    </div>

                    <div class="video-modal__body" :style="bodyStyle"
                        :class="{ 'is-no-video': !hasVideo, 'is-no-content': !hasContent }">
                        <!-- 左侧：视频 + 章节（桌面端在左列；手机端取消包裹、纵向堆叠） -->
                        <div class="video-modal__left">
                            <!-- 视频（全端固定不滚动） -->
                            <div v-if="hasVideo" class="video-modal__video-wrapper" :style="videoWrapperStyle">
                                <video ref="videoRef" class="video-modal__video" :src="src"
                                    :poster="poster || undefined" :autoplay="autoplay" controls playsinline
                                    webkit-playsinline preload="metadata" controlslist="nodownload" />
                            </div>

                            <!-- 章节：视频下方横向滚动，无标题 -->
                            <nav v-if="chapters.length" class="video-modal__chapters">
                                <ul ref="chapterListRef" class="video-modal__chapter-list">
                                    <li v-for="(chapter, idx) in chapters" :key="idx" class="video-modal__chapter-item"
                                        :class="{ 'is-active': activeChapter === idx }"
                                        @click="seekTo(chapter.time, idx)">
                                        <span class="video-modal__chapter-name">{{ chapter.name }}</span>
                                        <span class="video-modal__chapter-time">{{ formatTime(chapter.time) }}</span>
                                    </li>
                                </ul>
                            </nav>
                        </div>

                        <!-- 富文本图文（白色底色，可滚动；桌面端在视频右侧） -->
                        <div v-if="hasContent" ref="contentRef" class="video-modal__content" v-html="processedContent"
                            @scroll="handleContentScroll" @click="handleContentClick"></div>
                    </div>

                    <!-- 回到顶部固钉 -->
                    <button v-if="showBackToTop" class="video-modal__back-to-top" type="button" @click="scrollToTop">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
                            stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="18 15 12 9 6 15"></polyline>
                        </svg>
                        <span>{{ t('tutorial.backToVideo') }}</span>
                    </button>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import type { VideoChapter } from '@/stores/videoModal'
import X from '@/assets/icons/x.svg'

const { t } = useI18n()

const props = withDefaults(defineProps<{
    visible: boolean
    src: string
    poster?: string
    title?: string
    autoplay?: boolean
    content?: string
    chapters?: VideoChapter[]
}>(), {
    poster: '',
    title: '',
    autoplay: true,
    content: '',
    chapters: () => []
})

const emit = defineEmits<{
    (e: 'close'): void
}>()

const videoRef = ref<HTMLVideoElement | null>(null)
const contentRef = ref<HTMLDivElement | null>(null)
const chapterListRef = ref<HTMLUListElement | null>(null)
const activeChapter = ref(-1)

const hasContent = computed(() => props.content !== '<p><br></p>' && props.content)


const hasVideo = computed(() => Boolean(props.src))

/* 富文本中的播放占位符：${PLAY:12S} / ${PLAY:1:30} / ${PLAY:1:02:30} */
const PLAY_TOKEN_REGEX = /\$\{PLAY:([^}]+)\}/gi

/** 将 12S / 1:30 / 1:02:30 等格式解析为秒数 */
function parsePlayTime(raw: string): number {
    const s = String(raw).trim().toLowerCase().replace(/s$/, '')
    if (!s) return 0
    if (s.includes(':')) {
        const parts = s.split(':').map((p) => Number(p))
        let sec = 0
        for (const p of parts) sec = sec * 60 + (isNaN(p) ? 0 : p)
        return sec
    }
    return Number(s) || 0
}

/** 把 ${PLAY:xxx} 替换为带 data-play-time 的播放按钮 */
const processedContent = computed(() => {
    if (!props.content) return ''
    const playText = t('tutorial.play')
    return props.content.replace(PLAY_TOKEN_REGEX, (_m, time: string) => {
        const sec = parsePlayTime(time)
        return `<button type="button" class="tutorial-play-btn" data-play-time="${sec}">▶ ${playText}</button>`
    })
})

/** 富文本内点击：命中播放按钮则跳转视频到对应时间 */
function handleContentClick(e: MouseEvent): void {
    const target = (e.target as HTMLElement).closest('[data-play-time]') as HTMLElement | null
    if (!target) return
    const time = Number(target.dataset.playTime)
    if (isNaN(time)) return
    seekTo(time)
}

/* 滚动联动：手机端上滑内容时缩小上方视频，下滑还原 */
const MOBILE_BREAKPOINT = 1024
const COLLAPSE_DISTANCE = 160 // 滚动多少像素完全收起
const VIDEO_MAX_VH = 42 // 手机端视频最大高度(vh)
const VIDEO_MIN_VH = 10 // 收起后最小高度(vh)

const isMobile = ref(false)
const scrollY = ref(0)
let mql: MediaQueryList | null = null

/* 手机端返回键拦截：弹窗打开时 pushState 一条记录，返回键触发 popstate 关闭弹窗 */
const HISTORY_STATE_KEY = '__tutorialVideoModal'
let fromPopstate = false
let closed = false

function syncIsMobile(): void {
    isMobile.value = mql ? mql.matches : window.innerWidth < MOBILE_BREAKPOINT
}

const collapseProgress = computed(() => {
    if (!isMobile.value) return 0
    return Math.min(scrollY.value / COLLAPSE_DISTANCE, 1)
})

const videoWrapperStyle = computed(() => {
    if (collapseProgress.value <= 0) return {}
    const maxH = VIDEO_MAX_VH - collapseProgress.value * (VIDEO_MAX_VH - VIDEO_MIN_VH)
    return { maxHeight: `${maxH}vh` }
})

/* 收起进度（0~1）注入到 CSS，驱动章节同步缩小 */
const bodyStyle = computed(() => ({ '--collapse': String(collapseProgress.value) }))

function handleContentScroll(e: Event): void {
    const el = e.target as HTMLDivElement
    scrollY.value = el.scrollTop
}

const showBackToTop = computed(() => hasContent.value && scrollY.value > 60 && isMobile.value)

function scrollToTop(): void {
    const el = contentRef.value
    if (!el) return
    el.scrollTo({ top: 0, behavior: 'smooth' })
}

onMounted(() => {
    if (typeof window === 'undefined') return
    mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    syncIsMobile()
    mql.addEventListener?.('change', syncIsMobile)
})

function formatTime(seconds: number): string {
    const total = Math.max(0, Math.floor(Number(seconds) || 0))
    const m = Math.floor(total / 60)
    const s = total % 60
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function seekTo(time: number, idx = -1): void {
    const el = videoRef.value
    if (!el) return
    const t = Math.max(0, Number(time) || 0)
    if (el.readyState >= 1) {
        el.currentTime = t
    } else {
        el.addEventListener(
            'loadedmetadata',
            () => {
                if (videoRef.value) videoRef.value.currentTime = t
            },
            { once: true }
        )
    }
    if (el.paused) {
        el.play().catch(() => { })
    }
    if (idx >= 0) {
        activeChapter.value = idx
        scrollActiveChapterIntoView(idx)
    }
}

/** 同步滚动章节目录，使当前激活章节可见（横向滚动） */
function scrollActiveChapterIntoView(idx: number): void {
    const list = chapterListRef.value
    if (!list || idx < 0) return
    const target = list.children[idx] as HTMLElement | undefined
    if (!target) return
    const listRect = list.getBoundingClientRect()
    const itemRect = target.getBoundingClientRect()
    if (itemRect.left < listRect.left || itemRect.right > listRect.right) {
        list.scrollTo({
            left: target.offsetLeft - list.clientWidth / 2 + target.offsetWidth / 2,
            behavior: 'smooth'
        })
    }
}

function handleClose(): void {
    // 非返回键触发的关闭：回退历史栈，移除 pushState 插入的记录
    if (!fromPopstate && typeof window !== 'undefined' && history.state?.[HISTORY_STATE_KEY]) {
        history.back()
    }
    closeModal()
}

/** 浏览器返回键拦截：popstate 触发时关闭弹窗 */
function onPopstate(): void {
    fromPopstate = true
    closeModal()
    fromPopstate = false
}

/** 真正执行关闭：暂停视频、重置状态、通知父组件（防重复） */
function closeModal(): void {
    if (closed) return
    closed = true
    if (videoRef.value) {
        videoRef.value.pause()
        videoRef.value.currentTime = 0
    }
    activeChapter.value = -1
    emit('close')
}

function handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && props.visible) {
        handleClose()
    }
}

function syncActiveChapter(): void {
    const el = videoRef.value
    if (!el || props.chapters.length === 0) return
    const current = el.currentTime
    let active = -1
    props.chapters.forEach((ch, idx) => {
        if (current >= (Number(ch.time) || 0)) active = idx
    })
    if (active !== activeChapter.value) {
        activeChapter.value = active
        scrollActiveChapterIntoView(active)
    }
}

watch(
    () => props.visible,
    (val) => {
        if (val) {
            closed = false
            document.body.style.overflow = 'hidden'
            document.addEventListener('keydown', handleKeydown)
            window.addEventListener('popstate', onPopstate)
            // 插入一条历史记录，用于拦截手机端返回键
            history.pushState({ [HISTORY_STATE_KEY]: true }, '')
            // 重置滚动与视频收起状态
            scrollY.value = 0
            nextTick(() => {
                if (contentRef.value) contentRef.value.scrollTop = 0
            })
            if (hasVideo.value) {
                nextTick(() => {
                    const el = videoRef.value
                    if (el) {
                        el.addEventListener('timeupdate', syncActiveChapter)
                        el.addEventListener('seeked', syncActiveChapter)
                    }
                })
            }
        } else {
            document.body.style.overflow = ''
            document.removeEventListener('keydown', handleKeydown)
            window.removeEventListener('popstate', onPopstate)
            fromPopstate = false
            const el = videoRef.value
            if (el) {
                el.removeEventListener('timeupdate', syncActiveChapter)
                el.removeEventListener('seeked', syncActiveChapter)
            }
        }
    }
)

onBeforeUnmount(() => {
    document.body.style.overflow = ''
    document.removeEventListener('keydown', handleKeydown)
    window.removeEventListener('popstate', onPopstate)
    // 组件销毁时若仍有我们插入的历史记录，回退掉
    if (typeof window !== 'undefined' && history.state?.[HISTORY_STATE_KEY]) {
        history.back()
    }
    mql?.removeEventListener?.('change', syncIsMobile)
    const el = videoRef.value
    if (el) {
        el.removeEventListener('timeupdate', syncActiveChapter)
        el.removeEventListener('seeked', syncActiveChapter)
    }
})
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

$orange: #ff6d00;

.video-modal__overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    overflow: hidden;
}

.video-modal__container {
    position: relative;
    width: 100%;
    max-width: 1100px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.video-modal__title {
    color: #fff;
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0;
    padding-right: 1rem;
    line-height: 1.4;
    flex-shrink: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (min-width: $breakpoint-md) {
        font-size: 1.5rem;
    }
}

/* 头部：标题与关闭按钮同行 */
.video-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 1rem;
    flex-shrink: 0;
}

/* 主体：手机端纵向；桌面端左列(视频+章节)、右列(富文本) */
.video-modal__body {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex: 1;
    min-height: 0;

    &.is-no-content {
        justify-content: center;
    }

    @media (min-width: $breakpoint-lg) {
        flex-direction: row;

        &.is-no-video,
        &.is-no-content {
            flex-direction: column;
            justify-content: flex-start;
        }
    }
}

/* 左侧：手机端 display:contents 让视频/章节作为 body 直接子级纵向堆叠；
   桌面端作为左列，内部视频在上、章节在下 */
.video-modal__left {
    display: contents;

    @media (min-width: $breakpoint-lg) {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        flex: 0 0 55%;
        min-width: 0;

        .video-modal__body.is-no-content &,
        .video-modal__body.is-no-video & {
            flex: 1 1 100%;
        }
    }
}

/* 视频：全端固定不滚动 */
.video-modal__video-wrapper {
    position: relative;
    width: 100%;
    background: #000;
    border-radius: 0.75rem;
    overflow: hidden;
    aspect-ratio: 16 / 9;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.4);
    max-height: 42vh;
    flex-shrink: 0;

    @media (min-width: $breakpoint-md) {
        max-height: 55vh;
    }
}

.video-modal__video {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
}

/* 章节：视频下方，横向滚动，无标题 */
.video-modal__chapters {
    min-width: 0;
    flex-shrink: 0;
}

.video-modal__chapter-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    gap: calc(0.5rem - 0.15rem * var(--collapse, 0));
    scrollbar-width: thin;

    &::-webkit-scrollbar {
        height: 4px;
    }
}

.video-modal__chapter-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.125rem;
    padding: calc(0.5rem - 0.15rem * var(--collapse, 0)) calc(0.875rem - 0.3rem * var(--collapse, 0));
    border-radius: 0.5rem;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
    color: $color-text-secondary;
    background: #fff;
    flex-shrink: 0;
    white-space: nowrap;

    &:hover {
        background: $color-bg-tertiary;
        color: $color-text;
    }

    &.is-active {
        background: $orange;
        color: #fff;

        .video-modal__chapter-time {
            color: rgba(255, 255, 255, 0.85);
        }
    }
}

.video-modal__chapter-name {
    font-size: calc(#{$font-size-sm} - 0.0625rem * var(--collapse, 0));
    font-weight: 600;
    line-height: 1.3;
}

.video-modal__chapter-time {
    font-size: 0.75rem;
    color: $color-text-placeholder;
    font-variant-numeric: tabular-nums;
    line-height: 1.3;
    opacity: calc(1 - var(--collapse, 0));
    max-height: calc(1rem * (1 - var(--collapse, 0)));
    overflow: hidden;
}

/* 富文本图文：桌面端在视频右侧；白色底色，过长滚动 */
.video-modal__content {
    background: #fff;
    border-radius: 0.75rem;
    padding: 1.25rem;
    color: $color-text;
    font-size: $font-size-sm;
    line-height: 1.7;
    word-break: break-word;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;

    @media (min-width: $breakpoint-lg) {
        flex: 1;
        min-width: 0;
    }

    :deep(img) {
        max-width: 100%;
        height: auto;
        border-radius: 0.5rem;
    }

    :deep(p) {
        margin: 0 0 0.75rem;
    }

    :deep(h1),
    :deep(h2),
    :deep(h3),
    :deep(h4) {
        margin: 0 0 0.5rem;
        font-weight: 700;
    }

    :deep(ul),
    :deep(ol) {
        padding-left: 1.25rem;
        margin: 0 0 0.75rem;
    }

    :deep(a) {
        color: $color-primary;
        text-decoration: underline;
    }

    :deep(.tutorial-play-btn) {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        margin: 0 0.125rem;
        padding: 0.2rem 0.65rem;
        border: none;
        border-radius: 999px;
        background: $orange;
        color: #fff;
        font-size: 0.8125rem;
        font-weight: 600;
        line-height: 1.4;
        cursor: pointer;
        vertical-align: middle;
        transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(255, 109, 0, 0.35);
        }

        &:active {
            transform: translateY(0);
            opacity: 0.9;
        }
    }
}

.video-modal__close {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s, transform 0.2s;

    @media (min-width: $breakpoint-md) {
        width: 44px;
        height: 44px;
    }

    svg {
        width: 20px;
        height: 20px;
    }

    &:hover {
        background: rgba(255, 255, 255, 0.3);
        transform: scale(1.08);
    }

    &:active {
        transform: scale(0.95);
    }
}

/* 回到顶部固钉：右下角固定显示"看视频" */
.video-modal__back-to-top {
    position: absolute;
    right: 1rem;
    bottom: 1rem;
    z-index: 20;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem 0.875rem;
    border: none;
    border-radius: 999px;
    background: $orange;
    color: #fff;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 6px 18px rgba(255, 109, 0, 0.4);
    transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;

    svg {
        flex-shrink: 0;
    }

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 24px rgba(255, 109, 0, 0.5);
    }

    &:active {
        transform: translateY(0);
    }
}

/* Transition */
.video-modal-enter-active,
.video-modal-leave-active {
    transition: opacity 0.3s ease;
}

.video-modal-enter-from,
.video-modal-leave-to {
    opacity: 0;
}

.video-modal-enter-active .video-modal__container,
.video-modal-leave-active .video-modal__container {
    transition: transform 0.3s ease;
}

.video-modal-enter-from .video-modal__container,
.video-modal-leave-to .video-modal__container {
    transform: scale(0.92);
}

/* Mobile */
@media (max-width: $breakpoint-md) {
    .video-modal__overlay {
        padding: 0;
        align-items: stretch;
    }

    .video-modal__container {
        max-width: 100%;
        max-height: 100vh;
        padding: 0.75rem;
    }

    .video-modal__header {
        margin-bottom: 0.75rem;
    }

    .video-modal__title {
        font-size: 1.125rem;
    }

    .video-modal__video-wrapper {
        border-radius: 0.5rem;
    }
}
</style>
